import {
  Avatar,
  Box,
  Card,
  CardContent,
  Grid,
  Snackbar,
  Tooltip,
  Typography,
  IconButton,
  Alert as MuiAlert,
  Paper,
  Tab,
  Tabs,
  Container
} from '@mui/material'
import {
  Edit as EditIcon,
  EmailOutlined,
  Lock as LockIcon,
  PhoneCallback
} from '@mui/icons-material'
import React, { useEffect, useState } from 'react'
import { MapPin } from 'react-feather'
import { useNavigate } from 'react-router-dom'
import styled from '@emotion/styled'
import ProfilePasswordDrawer from './user/profilePasswordDrawer'
import UserProfileDrawer from './user/userProfileDrawer'
import BusinessDialog from '../business/business/businessDialog'
import {
  SnackBarHideDuration,
  SnackBarTransition,
  stringAvatar
} from '../../utils/ui_utils'
import {
  useBusinessUpdate,
  useFetchBusinessById
} from '../../modules/business/businessHooks'
import {
  useFetchUserById,
  useResetPassword,
  useUpdateProfile
} from '../../modules/user/userHooks'
import ListTypeShimmer from '../../utils/ui_components/shimmers/listTypeShimmer'
import { getUserType } from '../../components/AuthGuard'
import _ from 'lodash'

const Alert = styled(MuiAlert)(({ theme }) => ({
  margin: theme.spacing(1)
}))

const ProfileHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(120deg, #2196f3 0%, #21cbf3 100%)',
  color: 'white',
  padding: theme.spacing(3),
  borderRadius: '8px 8px 0 0',
  position: 'relative'
}))

const InfoCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
}))

function TabPanel(props) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

function UserProfile() {
  const [drawerState, setDrawerState] = useState(false)
  const [drawerBusinessState, setDrawerBusinessState] = useState(false)
  const [resetPasswordDrawerState, setResetPasswordDrawerState] =
    useState(false)
  const [tabValue, setTabValue] = useState(0)
  const [snackbar, setSnackBar] = useState(false)
  const [PopUpMessage, setPopUpMessage] = useState(null)
  const [severity, setSeverity] = useState(null)

  const { userUpdateProfile } = useUpdateProfile()
  const {
    userFetchById,
    userFetchByIdData,
    userFetchByIdError,
    userFetchByIdloading
  } = useFetchUserById()
  const { userResetPassword } = useResetPassword()
  const {
    fetchBusinessByApplication,
    fetchBusinessByApplicationData,
    fetchBusinessByApplicationError,
    fetchBusinessByApplicationLoading
  } = useFetchBusinessById()
  const { businessUpdateData, businessUpdateError, updateBusiness } =
    useBusinessUpdate()

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const handleSnackbarClose = () => {
    setSnackBar(false)
  }

  const toastMessage = (message, severity) => {
    setPopUpMessage(message)
    setSeverity(severity)
    setSnackBar(true)
  }

  const history = useNavigate()

  useEffect(() => {
    userFetchById()
    fetchBusinessByApplication()
  }, [])

  const userType = getUserType()

  if (userFetchByIdError || fetchBusinessByApplicationError)
    return <Typography color="error">Error loading data</Typography>
  if (userFetchByIdloading || fetchBusinessByApplicationLoading)
    return <ListTypeShimmer />

  return (
    <Container maxWidth="lg">
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', mb: 4 }}>
        <ProfileHeader>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Avatar {...stringAvatar(userFetchByIdData?.first_name ?? '')} />
            </Grid>
            <Grid item xs>
              <Typography variant="h4">
                {userFetchByIdData?.first_name} {userFetchByIdData?.last_name}
              </Typography>
              <Typography variant="subtitle1">
                {userFetchByIdData?.email}
              </Typography>
            </Grid>
            <Grid item>
              <Tooltip title="Change Password">
                <IconButton
                  color="inherit"
                  onClick={() => setResetPasswordDrawerState(true)}
                >
                  <LockIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </ProfileHeader>

        <Tabs value={tabValue} onChange={handleTabChange} centered>
          {userType !== 'HAdmin' && <Tab label="Business Info" />}
          {userType === 'FOwner' && <Tab label="Franchise Info" />}
          <Tab label="Personal Info" />
        </Tabs>

        <TabPanel value={tabValue} index={userType === 'FOwner' ? 2 : 1}>
          <Grid container spacing={3} justifyContent={'center'}>
            <Grid item xs={12} md={6} mb={2}>
              <InfoCard>
                <CardContent>
                  <Grid
                    container
                    spacing={2}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                  >
                    <Grid item>
                      <Typography variant="h6">
                        {userFetchByIdData?.first_name}{' '}
                        {userFetchByIdData?.last_name}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Tooltip title="Edit Profile">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => setDrawerState(true)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                  <Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item>
                        <EmailOutlined fontSize="small" />{' '}
                      </Grid>
                      <Grid item>{userFetchByIdData?.email}</Grid>
                    </Grid>
                  </Typography>
                  <Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item>
                        <PhoneCallback fontSize="small" />{' '}
                      </Grid>
                      <Grid item>{userFetchByIdData?.mobile}</Grid>
                    </Grid>
                  </Typography>
                  {userFetchByIdData?.address !== '' && (
                    <Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item>
                          <MapPin fontSize="small" />
                        </Grid>
                        <Grid item>{userFetchByIdData?.address}</Grid>
                      </Grid>
                    </Typography>
                  )}
                </CardContent>
              </InfoCard>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3} justifyContent={'center'}>
            <Grid item xs={12} md={6} mb={2}>
              <InfoCard>
                <CardContent>
                  <Grid
                    container
                    spacing={2}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                  >
                    <Grid item>
                      <Typography variant="h6">
                        {fetchBusinessByApplicationData?.name}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Tooltip title="Edit Business">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => setDrawerBusinessState(true)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                  <Grid container sx={{ gap: 2 }}>
                    <Typography gutterBottom varient="h6">
                      Business Code :{' '}
                      {fetchBusinessByApplicationData?.business_code}
                    </Typography>
                  </Grid>
                  <Grid container sx={{ gap: 2 }}>
                    <Typography gutterBottom varient="h6">
                      Dealer Code :{' '}
                      {fetchBusinessByApplicationData?.dealer_code ?? ''}
                    </Typography>
                  </Grid>
                  <Grid container sx={{ gap: 2 }}>
                    <Typography gutterBottom varient="body2">
                      GSTN : {fetchBusinessByApplicationData?.gstn ?? ''}
                    </Typography>
                  </Grid>
                  <Grid container sx={{ gap: 2 }}>
                    <Typography varient="body2" gutterBottom>
                      CIN : {fetchBusinessByApplicationData?.cin ?? ''}
                    </Typography>
                  </Grid>
                  <Grid container sx={{ gap: 2 }}>
                    <Typography varient="body2" gutterBottom>
                      Fax : {fetchBusinessByApplicationData?.fax ?? ''}
                    </Typography>
                  </Grid>
                  <Grid container sx={{ gap: 2 }} mb={2}>
                    <PhoneCallback />
                    <Typography varient="body2" gutterBottom>
                      {fetchBusinessByApplicationData?.telephone}
                    </Typography>
                  </Grid>
                  <Grid container sx={{ gap: 2 }}>
                    <MapPin />
                    <Typography
                      varient="body2"
                      gutterBottom
                      style={{ whiteSpace: 'pre-wrap' }}
                    >
                      {fetchBusinessByApplicationData?.address} {'\n'}
                      {fetchBusinessByApplicationData?.city}{' '}
                      {fetchBusinessByApplicationData?.pin ?? ''} {'\n'}
                      {fetchBusinessByApplicationData?.state}{' '}
                      {fetchBusinessByApplicationData?.country}{' '}
                    </Typography>
                  </Grid>
                </CardContent>
              </InfoCard>
            </Grid>
          </Grid>
        </TabPanel>

        {userType === 'FOwner' && (
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={3} justifyContent={'center'}>
              <Grid item xs={12} md={6} mb={2}>
                <InfoCard>
                  <CardContent>
                    <Typography variant={'subtitle1'} fontWeight={'bold'}>
                      {userFetchByIdData?.outlet
                        ?.map((o) => o.sub_business_name)
                        .join(' | ')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Outlets:{' '}
                      {userFetchByIdData?.outlet
                        ?.map((o) => o.name)
                        .join(' | ')}
                    </Typography>
                  </CardContent>
                </InfoCard>
              </Grid>
            </Grid>
          </TabPanel>
        )}
      </Paper>

      <ProfilePasswordDrawer
        visible={resetPasswordDrawerState}
        title="Change Password"
        onSubmit={(value) => {
          let values = _.cloneDeep(value)
          delete values.confirmPassword
          userResetPassword(values, async (success, error) => {
            if (error) toastMessage('Password is not updated', 'error')
            else {
              toastMessage('Password updated successfully.', 'success')
              setResetPasswordDrawerState(false)
              setTimeout(() => {
                history('/auth/sign-in')
              }, 1500)
            }
          })
        }}
        onClose={() => setResetPasswordDrawerState(false)}
        okText="Save"
      />

      <UserProfileDrawer
        visible={drawerState}
        okText="Update"
        onSubmit={(value) => {
          let values = _.cloneDeep(value)
          delete values._id
          delete values.confirmPassword
          delete values.email
          delete values.outlet
          delete values.mobile
          delete values.user_name
          userUpdateProfile(values, (success, error) => {
            if (error) toastMessage('Unable to update profile details', 'error')
            else {
              toastMessage('Profile details updated', 'success')
              setDrawerState(false)
              userFetchById()
            }
          })
        }}
        onClose={() => setDrawerState(false)}
        initialValues={userFetchByIdData}
        title="Update Profile"
      />

      <BusinessDialog
        visible={drawerBusinessState}
        title="Edit Business Details"
        okText="Update"
        mode="Edit"
        onSubmit={async (value) => {
          let values = _.cloneDeep(value)
          values.id = values._id
          delete values._id
          delete values.admin_id
          if (values.cin === '') delete values.cin
          delete values.dealer_code
          await updateBusiness(values)
          if (businessUpdateError) {
            toastMessage('Update failed.', 'error')
          } else if (businessUpdateData) {
            toastMessage('Business details updated', 'success')
            setDrawerBusinessState(false)
            fetchBusinessByApplication()
          }
        }}
        onClose={() => setDrawerBusinessState(false)}
        initialValues={fetchBusinessByApplicationData}
      />

      <Snackbar
        open={snackbar}
        autoHideDuration={SnackBarHideDuration}
        onClose={handleSnackbarClose}
        TransitionComponent={SnackBarTransition}
      >
        <Alert onClose={handleSnackbarClose} severity={severity}>
          {PopUpMessage}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default UserProfile
